import * as React from 'react';
import { faFacebookF } from '@fortawesome/free-brands-svg-icons/faFacebookF';
import { faPinterest } from '@fortawesome/free-brands-svg-icons/faPinterest';
import { faTachometerAlt } from '@fortawesome/pro-solid-svg-icons/faTachometerAlt';
import { faTwitter } from '@fortawesome/free-brands-svg-icons/faTwitter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import {
    getAllUpcomingAuctionsUrl,
    getAuctioneerDirectoryUrl,
    getAuctionResultsUrl,
    getAuctionsNearMePageUrl,
    getCookiePolicyUrl,
    getLiveAuctioneersCareersUrl,
    getPrivacyUrl,
    getTermsAndConditionsUrl,
} from '@/utils/urls';
import { getBaseUrl, getPartnersBaseUrl } from '@/redux/modules/config';
import { openFeedbackModal, openHealthcheckModal } from '@/redux/modules/modal';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import classNames from 'classnames';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import styles from './Support.module.scss';

const Support = () => {
    const dispatch = useAppDispatch();
    const baseUrl = useAppSelector(getBaseUrl);
    const partnersBaseUrl = useAppSelector(getPartnersBaseUrl);

    const feedbackModal = React.useCallback(() => dispatch(openFeedbackModal()), [dispatch]);

    const healthcheckModal = React.useCallback(() => {
        dispatch(openHealthcheckModal());
    }, [dispatch]);

    return (
        <div className={styles.support}>
            <div className={styles.container}>
                <div className={styles.column}>
                    <div className={styles.wrapper}>
                        <div className={styles['column-title']}>
                            <FormattedMessage id="company" />
                        </div>
                        <ul>
                            <li>
                                <Link route to="/about">
                                    <FormattedMessage id="about" />
                                </Link>
                            </li>
                            <li>
                                <Link rel="noopener" target="_blank" to={`${baseUrl}/news/`}>
                                    <FormattedMessage id="auction_central_news" />
                                </Link>
                            </li>
                            <li>
                                <Link rel="noopener" target="_blank" to={getLiveAuctioneersCareersUrl()}>
                                    <FormattedMessage id="careers" />
                                </Link>
                            </li>
                            <li>
                                <Link rel="noopener" target="_blank" to="https://help.liveauctioneers.com/">
                                    <FormattedMessage id="support_center" />
                                </Link>
                            </li>
                            <li>
                                <Link onClick={feedbackModal} rel="noopener" target="_blank">
                                    <FormattedMessage id="feedback.footer" />
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className={styles.column}>
                    <div className={styles.wrapper}>
                        <div className={styles['column-title']}>
                            <FormattedMessage id="winning" />
                        </div>
                        <ul>
                            <li>
                                <Link route to={getAllUpcomingAuctionsUrl()}>
                                    <FormattedMessage id="auction_calendar" />
                                </Link>
                            </li>
                            <li>
                                <Link route to={getAuctioneerDirectoryUrl()}>
                                    <FormattedMessage id="auction_house_directory" />
                                </Link>
                            </li>
                            <li>
                                <Link route to={getAuctionResultsUrl()}>
                                    <FormattedMessage id="auction_price_results" />
                                </Link>
                            </li>
                            <li>
                                <Link route to={getAuctionsNearMePageUrl()}>
                                    <FormattedMessage id="auctions_near_me" />
                                </Link>
                            </li>
                            <li>
                                <Link route to="/how-auctions-work">
                                    <FormattedMessage id="how_auctions_work" />
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className={styles.column}>
                    <div className={styles.wrapper}>
                        <div className={styles['column-title']}>
                            <FormattedMessage id="selling" />
                        </div>
                        <ul>
                            <li>
                                <Link rel="nofollow noopener" target="_blank" to={`${partnersBaseUrl}/login`}>
                                    <FormattedMessage id="auctioneer_sign_in" />
                                </Link>
                            </li>
                            <li>
                                <a href={`${baseUrl}/pages/sell/`} target="_blank">
                                    <FormattedMessage id="become_a_seller" />
                                </a>
                            </li>
                            <li>
                                <Link route to="/consignments">
                                    <FormattedMessage id="consign_an_item" />
                                </Link>
                            </li>
                            <li>
                                <Link
                                    rel="nofollow noopener"
                                    target="_blank"
                                    to="https://www.liveauctioneers.com/pages/resource-center/"
                                >
                                    <FormattedMessage id="seller_resource_center" />
                                </Link>
                            </li>
                            <li>
                                <Link route to="/seller-reviews">
                                    <FormattedMessage id="seller_reviews" />
                                </Link>
                            </li>
                            <li>
                                <Link route to="/features">
                                    <FormattedMessage id="why_sell" />
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div className={styles['social-icons']}>
                <Link
                    className={styles.icon}
                    name="LiveAuctioneers on Twitter"
                    rel="noopener"
                    target="_blank"
                    to="https://twitter.com/liveauctioneers/"
                >
                    <FontAwesomeIcon icon={faTwitter} />
                </Link>
                <Link
                    className={styles.icon}
                    name="LiveAuctioneers on Facebook"
                    rel="noopener"
                    target="_blank"
                    to="https://www.facebook.com/LiveAuctioneers/"
                >
                    <FontAwesomeIcon icon={faFacebookF} />
                </Link>
                <Link
                    className={styles.icon}
                    name="LiveAuctioneers on Pinterest"
                    rel="noopener"
                    target="_blank"
                    to="https://www.pinterest.com/liveauctioneers/"
                >
                    <FontAwesomeIcon icon={faPinterest} />
                </Link>
            </div>
            <div className={styles['container-copyright']}>
                <div className={styles.copyright}>
                    <span>©</span> <FormattedMessage id="copyright" values={{ year: new Date().getFullYear() }} />
                    <Link route to={getCookiePolicyUrl()}>
                        <FormattedMessage id="cookie_policy" />
                    </Link>
                    <Link route to={getTermsAndConditionsUrl()}>
                        <FormattedMessage id="terms_and_conditions" />
                    </Link>
                    <Link route to={getPrivacyUrl()}>
                        <FormattedMessage id="privacy_policy" />
                    </Link>
                    <FontAwesomeIcon className={styles.health} icon={faTachometerAlt} onClick={healthcheckModal} />
                </div>
                <div className={classNames(styles.copyright, styles.google)}>
                    <FormattedMessage
                        id="footer.recaptcha.text"
                        values={{
                            privacy: (
                                <Link
                                    className={styles.googleLink}
                                    rel="nofollow noopener"
                                    target="_blank"
                                    to="https://policies.google.com/privacy"
                                >
                                    <FormattedMessage id="footer.recaptcha.privacy" />
                                </Link>
                            ),
                            tos: (
                                <Link
                                    className={styles.googleLink}
                                    rel="nofollow noopener"
                                    target="_blank"
                                    to="https://policies.google.com/terms"
                                >
                                    <FormattedMessage id="footer.recaptcha.tos" />
                                </Link>
                            ),
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

// because baseUrl and partnersBaseUrl will never update we can keep memo
export default React.memo(Support);
